import { Box, FormControlLabel, Grid, Switch } from '@mui/material';
import { INTEGERS_UNIT } from '../../../../utils/regex';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutocompleteComponent from '../../../../components/Autocomplete/Autocomplete';
import Button from '../../../../components/Button/Button';
import CdDrawer from '../../../../components/Drawer/CdDrawer';
import Input from '../../../../components/Input/Input';
import Loading from '../../../../components/Loading';
import TabPanel from './components/TabPanel';
import useUserSave from './hooks/useUserSave';
import usePermissions from '../../hooks/usePermissions';

function UserDrawerSave({ handleCloseDrawer, drawerAction, selectedItem }: any) {
    const {
        data,
        loading,
        scrollRef,
        addingUser,
        updatingUser,
        userCuilState,
        formattedRoles,
        validatingUserCuil,
        submitButtonValidation,
        loadingRolesNallDocTypes,
        clearData,
        scrollView,
        getFullName,
        handleSubmit,
        copyPermissions,
        handleChangeData,
        userCuilValidation,
        ...restProps
    } = useUserSave({
        drawerAction,
        selectedItem,
        handleCloseDrawer
    });
    const { clearPermissionForm, ...permisionProps } = usePermissions({
        data,
        selectedItem,
        drawerAction,
        scrollView,
        handleChangeData
    });

    return (
        <CdDrawer
            open={['new', 'edit'].includes(drawerAction)}
            onClose={() => {
                return;
            }}
            onCloseButton={() => {
                clearData();
                clearPermissionForm();
                handleCloseDrawer();
            }}
            title={selectedItem ? `Usuario ${getFullName(selectedItem?.row)}` : 'Nuevo usuario'}
            icon={<ArrowBackIcon />}
            onSubmit={drawerAction === 'new' ? handleSubmit : undefined}
            disabled={submitButtonValidation}
            loading={addingUser}
            styles={{ width: '780px' }}
        >
            <Box height="calc(100% - 19px)">
                <Grid container spacing={1} columnSpacing={2} p={1}>
                    {loading ? (
                        <Grid item xs={12}>
                            <Loading size="small" />
                        </Grid>
                    ) : (
                        <>
                            <Grid item xs={12} sx={{ '& h3': { mb: 0 } }}>
                                <h3>Datos del usuario</h3>
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '15px'
                                }}
                            >
                                <Input
                                    label="CUIL"
                                    name="cuilUsuario"
                                    fullWidth
                                    required
                                    value={data?.cuilUsuario || data?.cuil || ''}
                                    onChange={(e: any) => {
                                        let value = e.target.value;
                                        if (
                                            (INTEGERS_UNIT.test(value) || value === '') &&
                                            value?.length < 12
                                        ) {
                                            handleChangeData({
                                                target: {
                                                    name: 'cuilUsuario',
                                                    value: value
                                                }
                                            });
                                        }
                                    }}
                                    onKeyDown={(e: any) => {
                                        if (
                                            userCuilState?.state !== 'ok' ||
                                            userCuilState?.cuil !== data?.cuilUsuario
                                        ) {
                                            e.preventDefault();
                                            userCuilValidation();
                                        }
                                    }}
                                    disabled={drawerAction === 'edit'}
                                    className={
                                        userCuilState?.state === 'notfound' ? 'user-error' : ''
                                    }
                                    loading={validatingUserCuil}
                                />
                                {drawerAction === 'new' && (
                                    <Button
                                        disabled={userCuilState?.cuil === data?.cuilUsuario}
                                        onClick={userCuilValidation}
                                        style={{ padding: '8px 16px', borderRadius: '8px' }}
                                    >
                                        Validar
                                    </Button>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                <Input
                                    disabled
                                    label="Nombre"
                                    value={
                                        ((data?.cuilUsuario || data?.cuil) && data?.nombre) || ''
                                    }
                                    className="user-fullname"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Input
                                    disabled
                                    label="Apellido"
                                    value={
                                        ((data?.cuilUsuario || data?.cuil) && data?.apellido) || ''
                                    }
                                    className="user-fullname"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <AutocompleteComponent
                                    label="Rol"
                                    required
                                    options={formattedRoles}
                                    disabled={
                                        loadingRolesNallDocTypes ||
                                        !formattedRoles ||
                                        (drawerAction === 'new' &&
                                            (!formattedRoles || userCuilState?.state !== 'ok'))
                                    }
                                    loading={loadingRolesNallDocTypes}
                                    value={data?.idRol || ''}
                                    onChange={(value: any) => {
                                        handleChangeData({
                                            target: {
                                                name: 'idRol',
                                                value: value
                                            }
                                        });
                                        clearPermissionForm();
                                    }}
                                />
                            </Grid>
                            <Grid
                                item
                                xs={6}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    '& svg': {
                                        color: 'var(--alternative2)'
                                    }
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={data?.esAdmin || false}
                                            value={data?.esAdmin || false}
                                            disabled={
                                                drawerAction !== 'edit' &&
                                                userCuilState?.state !== 'ok'
                                            }
                                            name="esAdmin"
                                            onChange={handleChangeData}
                                            inputProps={{ 'aria-label': 'controlled' }}
                                        />
                                    }
                                    label="Es admin"
                                />
                            </Grid>
                            {drawerAction === 'edit' && (
                                <Grid item xs={12} display="flex" justifyContent="flex-end">
                                    <Button
                                        onClick={(e: any) => {
                                            if (e?.key === 'Enter') return;
                                            handleSubmit();
                                        }}
                                        disabled={submitButtonValidation}
                                        loading={updatingUser}
                                    >
                                        Guardar
                                    </Button>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <TabPanel
                                    data={data}
                                    selectedItem={selectedItem}
                                    clearData={clearData}
                                    drawerAction={drawerAction}
                                    copyPermissions={copyPermissions}
                                    scrollView={scrollView}
                                    {...restProps}
                                    {...permisionProps}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </Box>
            <div ref={scrollRef} />
        </CdDrawer>
    );
}

export default UserDrawerSave;
